type MemberRole = API.Member["role"];

type GroupMemberRemoval = {
    targetRole: MemberRole;
    isSelf: boolean;
    mayManageMembers: boolean;
    mayManageTargetRole: boolean;
};

type WorkspaceMemberRemoval = GroupMemberRemoval & {
    isWorkspaceMember: boolean;
};

type GroupRemovalGate = {
    titleKey:
        | "pages:workspacePage.tooltip.removeFromGroup"
        | "pages:workspacePage.tooltip.cannotManageMembers"
        | "sidepanels:groupPanel.tooltip.ownerCannotBeDeleted";
    mayRemove: boolean;
};

type WorkspaceRemovalGate = {
    titleKey:
        | "pages:workspacePage.tooltip.removeFromWorkspace"
        | "pages:workspacePage.tooltip.cannotManageMembers"
        | "pages:workspacePage.tooltip.memberIsNotWorkspaceMember"
        | "pages:workspacePage.tooltip.managersCannotDeleteOwners";
    mayRemove: boolean;
};

/**
 * Decides whether the signed-in user may remove a member from a group, and which tooltip explains a refusal.
 * The general "may manage members" gate is checked before the owner-specific one, so a role that cannot manage
 * members at all (a plain User, say) reads the general message rather than the owner-only wording, which then
 * only reaches actual Managers.
 */
export const getGroupMemberRemovalGate = (member: GroupMemberRemoval): GroupRemovalGate => {
    if (member.isSelf) {
        return { titleKey: "pages:workspacePage.tooltip.removeFromGroup", mayRemove: true };
    }
    if (!member.mayManageMembers) {
        return { titleKey: "pages:workspacePage.tooltip.cannotManageMembers", mayRemove: false };
    }
    if (member.targetRole === "Owner" && !member.mayManageTargetRole) {
        return { titleKey: "sidepanels:groupPanel.tooltip.ownerCannotBeDeleted", mayRemove: false };
    }

    return { titleKey: "pages:workspacePage.tooltip.removeFromGroup", mayRemove: true };
};

/**
 * Decides whether the signed-in user may remove a member from the workspace, and which tooltip explains a
 * refusal. Unlike the group gate, self-removal is open to a User only: an Owner or Manager removing themselves
 * still has to pass the ordinary permission gates, so a workspace cannot lose its last Owner by accident.
 */
export const getWorkspaceMemberRemovalGate = (member: WorkspaceMemberRemoval): WorkspaceRemovalGate => {
    if (!member.isWorkspaceMember) {
        return { titleKey: "pages:workspacePage.tooltip.memberIsNotWorkspaceMember", mayRemove: false };
    }
    if (member.targetRole === "User" && member.isSelf) {
        return { titleKey: "pages:workspacePage.tooltip.removeFromWorkspace", mayRemove: true };
    }
    if (!member.mayManageMembers) {
        return { titleKey: "pages:workspacePage.tooltip.cannotManageMembers", mayRemove: false };
    }
    if (member.targetRole === "Owner" && !member.mayManageTargetRole) {
        return { titleKey: "pages:workspacePage.tooltip.managersCannotDeleteOwners", mayRemove: false };
    }

    return { titleKey: "pages:workspacePage.tooltip.removeFromWorkspace", mayRemove: true };
};
